import { t } from 'i18next';
import * as yup from 'yup';

import {
  autocompleteOptionSchema,
  emailRegex,
  notRequiredPhoneSchema,
} from '~constants/regexConst';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';

// Most callers accept either an email or a phone for a contact; the account
// promotion flow (PromoteAccountDialog) requires email unconditionally, so the
// requirement is a factory parameter rather than a second hardcoded schema.
export const makeContactSchema = (requireEmail: boolean) =>
  yup.object().shape({
    // The backend stores ONE `contacts.name` column and only requires it to be
    // non-blank (`Company#primary_contact_required_fields`). Requiring both halves was
    // an FE invention that made two real records unsubmittable with no visible reason:
    // a single-token name ("Cher") leaves one half empty, and a contact whose name was
    // never set leaves both empty. The requirement belongs on the pair, so it is
    // asserted on `firstName` — the field the split always fills first.
    firstName: yup
      .string()
      .test(
        'contact-name-present',
        `${t('form_validation_errors.required', { field: t('form_fields.first_name') })}`,
        (firstName, context) =>
          Boolean(`${firstName ?? ''} ${context.parent?.lastName ?? ''}`.trim()),
      ),
    lastName: yup.string().notRequired(),
    email: requireEmail
      ? yup
          .string()
          .required(
            `${t('form_validation_errors.required', { field: t('form_fields.email') })}`,
          )
          .matches(emailRegex, `${t('form_validation_errors.invalid_email')}`)
      : yup
          .string()
          .matches(emailRegex, `${t('form_validation_errors.invalid_email')}`)
          .transform((val) => {
            if (!val?.length) {
              return undefined;
            }
            return val;
          })
          .notRequired()
          .when('phone', {
            is: (phone: string | undefined) => {
              return Boolean(!phone || !phone.trim().length);
            },
            then: () => {
              return yup
                .string()
                .required(
                  `${t('form_validation_errors.either_one_or_other', {
                    one: t('form_fields.email'),
                    other: t('form_fields.phone'),
                  })}`,
                )
                .matches(emailRegex, `${t('form_validation_errors.invalid_email')}`);
            },
          }),
    phone: notRequiredPhoneSchema.transform((val) => {
      if (!val || !val.trim().length) {
        return undefined;
      }
      return val;
    }),
  });

export const makeCompanyCreateSchema = (requireContactEmail = false) =>
  yup.object().shape({
    id: yup.string(),
    parentCompany: yup.object().shape({
      name: yup.string(),
      id: yup.string(),
    }),
    legalName: yup.string().required(
      `${t('form_validation_errors.required', {
        field: t('form_fields.legal_name'),
      })}`,
    ),
    doingBusinessName: yup.string().notRequired(),
    companyType: yup
      .string()
      .required(
        `${t('form_validation_errors.required', { field: t('form_fields.role') })}`,
      ),

    address: yup.object().shape({
      streetAddress: yup
        .string()
        .required(
          `${t('form_validation_errors.required', { field: t('form_fields.address') })}`,
        ),
      premise: yup.string().typeError(
        `${t('form_validation_errors.should_be_string', {
          field: t('form_fields.address_2'),
        })}`,
      ),
      locality: yup
        .string()
        .required(
          `${t('form_validation_errors.required', { field: t('form_fields.city') })}`,
        ),
      administrativeArea: yup.string().typeError(
        `${t('form_validation_errors.should_be_string', {
          field: t('form_fields.state'),
        })}`,
      ),
      postalCode: yup
        .string()
        .required(
          `${t('form_validation_errors.required', { field: t('form_fields.zip') })}`,
        ),
      country: yup
        .object()
        .typeError(
          `${t('form_validation_errors.required', { field: t('form_fields.country') })}`,
        )
        .required(
          `${t('form_validation_errors.required', { field: t('form_fields.country') })}`,
        ),
      lat: yup.number().nullable().notRequired(),
      lng: yup.number().nullable().notRequired(),
      // Carried, not edited: the autocomplete writes it and the deparse reads it.
      // It still belongs in the schema so every form field has an entry.
      placeId: yup.string().notRequired(),
    }),

    primaryContact: makeContactSchema(requireContactEmail),

    billingContact: makeContactSchema(requireContactEmail),

    defaultAddress: yup.mixed<AddressItem>().nullable().notRequired(),

    // The form's map-centre fields are `defaultLat`/`defaultLon`. The snake_case keys
    // that used to sit here matched nothing in `CompanyFormValues`, so three rules
    // validated permanently-undefined values while the real coordinates went unchecked.
    defaultLat: yup.number().nullable().notRequired(),
    defaultLon: yup.number().nullable().notRequired(),

    timeZone: yup.string().required(),
    dateFormat: yup.string().required(),
    timeFormat: yup.string().required(),
    systemOfMeasure: yup.string().required(),
    unitOfDistance: yup.object().shape({
      value: yup.string().required(),
      label: yup.string().required(),
    }),
    usageStatus: yup.string().required(
      `${t('form_validation_errors.required', {
        field: t('form_fields.usage_status'),
      })}`,
    ),
    billingStatus: yup.string().required(
      `${t('form_validation_errors.required', {
        field: t('form_fields.billing_status'),
      })}`,
    ),
    saasBillingPlan: yup.string().required(
      `${t('form_validation_errors.required', {
        field: t('form_fields.saas_billing_plan'),
      })}`,
    ),
    // Blank is valid: clearing the field unlinks the company from its Stripe customer.
    externalStripeCustomerId: yup.string().matches(/^cus_[a-zA-Z0-9]+$/, {
      excludeEmptyString: true,
      message: `${t('form_validation_errors.stripe_customer_id_format')}`,
    }),
    workWeekStartDay: autocompleteOptionSchema.notRequired(),
  });
